// src/components/OnboardingModal.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

function OnboardingModal({ isOpen, onClose, initialStep = 0 }) {
  const [currentStep, setCurrentStep] = useState(initialStep);
  const navigate = useNavigate();
  
  // Set step when prop changes
  useEffect(() => {
    setCurrentStep(initialStep);
  }, [initialStep]);
  
  // Define steps content
  const steps = [
    {
      title: "Welcome to Reel:Intro!",
      description: "Your subscription is now active! Let's get you set up with a few quick steps.",
      image: "hero-2.png",
      cta: "Let's Get Started",
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-blue-500" viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v2H7a1 1 0 100 2h2v2a1 1 0 102 0v-2h2a1 1 0 100-2h-2V7z" clipRule="evenodd" />
        </svg>
      )
    },
    {
      title: "Complete Your Profile",
      description: "Add a professional photo and social links so viewers can connect with you.",
      image: "manage-library.svg",
      cta: "Next: Record Video",
      action: () => {}, // No navigation action - already on profile page
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-blue-500" viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
        </svg>
      )
    },
    {
      title: "Record Your Intro Reel",
      description: "Create a short, engaging video that showcases your personality and skills.",
      image: "record-anywhere.svg",
      cta: "Next: Share Your Reel",
      action: () => navigate('/record'),
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-blue-500" viewBox="0 0 20 20" fill="currentColor">
          <path d="M2 6a2 2 0 012-2h6a2 2 0 012 2v8a2 2 0 01-2 2H4a2 2 0 01-2-2V6zM14.553 7.106A1 1 0 0014 8v4a1 1 0 00.553.894l2 1A1 1 0 0018 13V7a1 1 0 00-1.447-.894l-2 1z" />
        </svg>
      )
    },
    {
      title: "Share Your Reel",
      description: "Share your intro with anyone via a simple link - no login required for viewers!",
      image: "instant-sharing.svg",
      cta: "Get Started Now",
      action: () => onClose(),
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-blue-500" viewBox="0 0 20 20" fill="currentColor">
          <path d="M15 8a3 3 0 10-2.977-2.63l-4.94 2.47a3 3 0 100 4.319l4.94 2.47a3 3 0 10.895-1.789l-4.94-2.47a3.027 3.027 0 000-.74l4.94-2.47C13.456 7.68 14.19 8 15 8z" />
        </svg>
      )
    }
  ];
  
  // Handle next step
  const handleNext = () => {
    // Save current step to local storage
    const nextStep = currentStep + 1;
    localStorage.setItem('onboardingLastStep', nextStep >= steps.length ? 0 : nextStep);
    
    if (currentStep === steps.length - 1) {
      // Last step, close modal
      onClose();
    } else {
      // If the current step has an action, execute it
      if (steps[currentStep].action) {
        steps[currentStep].action();
      }
      // Move to next step
      setCurrentStep(nextStep);
    }
  };
  
  // Save current step when modal is closed
  const handleClose = () => {
    // Save current position before closing
    localStorage.setItem('onboardingLastStep', currentStep);
    onClose();
  };
  
  // Modal visibility control
  if (!isOpen) return null;
  
  const currentStepData = steps[currentStep];
  
  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-black bg-opacity-70 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full transform transition-all">
        {/* Progress indicators */}
        <div className="px-6 pt-6 pb-2">
          <div className="flex justify-between items-center">
            <div className="flex space-x-2">
              {steps.map((_, index) => (
                <div 
                  key={index}
                  className={`h-1.5 w-12 rounded-full ${
                    index <= currentStep ? 'bg-blue-500' : 'bg-gray-200'
                  }`}
                ></div>
              ))}
            </div>
            <button 
              onClick={handleClose}
              className="text-gray-500 hover:text-gray-700"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
        
        <div className="px-8 py-6">
          {/* Icon and title */}
          <div className="flex items-center mb-4">
            {currentStepData.icon}
            <h2 className="text-2xl font-bold text-gray-900 ml-3">{currentStepData.title}</h2>
          </div>
          
          {/* Main content */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
            {/* Left: Image */}
            <div className="flex justify-center">
              <img 
                src={currentStepData.image} 
                alt={`Step ${currentStep + 1}`} 
                className="max-h-64 object-contain"
              />
            </div>
            
            {/* Right: Description and button */}
            <div>
              <p className="text-gray-600 mb-6 text-lg">{currentStepData.description}</p>
              
              <button
                onClick={handleNext}
                className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center font-medium"
              >
                {currentStepData.cta}
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 ml-2" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default OnboardingModal;